import SCORM, {
  get,
  set,
  getFirm,
  isFirm,
  getScore,
  sendInsightsData,
  getCourseId
} from './SCORM';
import useEditMode from '../stores/useEditMode';
import useContent from '../stores/useContent';
import findTopWindow from './findTopWindow';
import useQuizzes from '../stores/useQuizzes';
import type {
  Metadata,
  InsightsData,
  SubmissionData,
  InsightType
} from '../../types/InsightsData';
import type { Suspend } from 'Suspend';
import bugAlert from './bugAlert';

const firm = getFirm();
const firmTerm =
  /^[a-zA-Z]/.test(firm) && !isFirm('REVIEW') && !isFirm('VINCI_ADMIN')
    ? firm.toLowerCase()
    : null;

const editorWindow = findTopWindow({
  withOpeners: true,
  predicate: w => 'getEditorDraft' in w
});

const getEditorDraft = (editorWindow as EditorWindow)?.getEditorDraft;

const findScormId: () => string = () => {
  const editorWindow = findTopWindow({
    withOpeners: true,
    predicate: w => 'VW_SCORM_ID' in w
  }) as any;
  return editorWindow?.VW_SCORM_ID || '';
};

const getBaseCourseId = (): number => {
  const content = useContent().compiled;
  return (content?.['base-course'] as number) ?? getCourseId();
};

/**
 * Saves insights data by combining the screen's insights-data with additional data.
 *
 * @param screen - The screen object containing id and optional insights-data
 * @param additionalData - Additional data object to merge with the screen's insights-data
 */
export function saveInsightsData(
  screen: { id: string; 'insights-data'?: InsightsData },
  additionalData: Record<string, any> = {}
): void {
  if (!screen['insights-data']) return;

  const obj = {
    ...screen['insights-data'],
    ...additionalData
  };

  // set the type to assessment if we specified an lo-id and we didn't specify a type (assessment questions)
  if (obj['lo-id'] && !obj['type']) {
    obj.type = 'assessment' as InsightType;
    console.log(
      `Safely inferring missing insights-data 'type' on screen: ${screen.id} from lo-id: ${obj['lo-id']}, type: ${obj.type}`
    );
  }

  // identify missing type and and infer from the template
  if (obj['insight-id'] && !obj['type']) {
    console.log(
      `Missing insights-data 'type': ${JSON.stringify(obj)}. Inferring from screen: ${screen.id}, template: ${screen['template']}`
    );
    switch (screen['template']) {
      case 'question':
      case 'grid-question':
      case 'ordering-question':
      case 'slider':
      case 'toggle':
        obj.type = 'mcq' as InsightType;
        break;

      case 'likert-question':
        obj.type = `likert-${screen['likert-data'].scale}` as InsightType;
        break;

      case 'textarea':
        obj.type = 'text' as InsightType;
        break;

      default:
        obj.type = 'mcq' as InsightType;
        break;
    }
    console.log('Inferred insights-data type', obj.type);
  }

  let errorMessage = `insights-data on screen: ${screen.id} has missing/invalid properties:`;
  let hasErrors = false;

  // catch any BAD data before we save/send it (missing both lo-id and insight-id, incorrect types, missing/invalid correct prop for its type etc.)
  if (!obj['lo-id'] && !obj['insight-id']) {
    errorMessage += ` lo-id or insight-id`;
    hasErrors = true;
  }
  if (!obj.type) {
    errorMessage += ` type`;
    hasErrors = true;
  }
  if (
    (obj.type === 'assessment' || obj.type === 'mcq') &&
    (obj.correct === undefined || obj.correct === null)
  ) {
    errorMessage += ` correct prop for type: ${obj.type}`;
    hasErrors = true;
  }
  if (
    obj.type.startsWith('likert-') &&
    (!obj['likert-value'] || !obj['likert-score-direction'])
  ) {
    errorMessage += ` likert-value or likert-score-direction`;
    hasErrors = true;
  }

  if (hasErrors) {
    console.error(errorMessage, `insights-data: ${JSON.stringify(obj)}`);
    bugAlert(`${errorMessage}\n\ninsights-data: ${JSON.stringify(obj)}`);
    return;
  }

  // only store assessment data once as we want their first set of assessment responses to be the one that is submitted
  if (obj.type === 'assessment' && getInsightsData(screen)) {
    return;
  }

  const isEditModeAvailable = useEditMode().available;

  // Don't save insights data if no firm is found or if course is in edit/authoring mode
  if (!firmTerm || isEditModeAvailable || getEditorDraft) {
    console.log(
      `Skipping insights data saving for screen: ${screen.id} because no firm code was found or we are in edit/authoring mode`
    );
    console.log(`insights-data: ${JSON.stringify(obj)}`);
    return;
  }

  set(`insightsData.insights.${screen.id}`, obj);
}

export function getInsightsData(screen: { id: string }): InsightsData {
  return get(`insightsData.insights.${screen.id}`) as InsightsData;
}

/**
 * Submits all insights data collected during the course.
 * This function collates all insights data from suspend.insightsData and submits it to the API.
 *
 * The function will:
 * - Skip submission if no firm is found
 * - Skip submission if course is in edit/authoring mode
 * - Skip submission if insights data has already been submitted
 * - Collect all insights data from suspend.insightsData and submit it to the API
 * - Save the submission timestamp to suspend.insightsData.metadata.submitted
 */
export async function submitInsightsData(): Promise<void> {
  const isEditModeAvailable = useEditMode().available;

  // Don't submit insights data if no firm is found
  if (!firmTerm) {
    console.log('Skipping insights data submission: no firm found');
    return;
  }

  // Don't submit insights data if course is in edit/authoring mode
  if (isEditModeAvailable || getEditorDraft) {
    console.log('Skipping insights data submission in edit/authoring mode');
    return;
  }

  const insightsData = get('insightsData') as Suspend['insightsData'];

  // Check if there's any insights data to submit
  if (
    !insightsData ||
    !insightsData?.insights ||
    Object.keys(insightsData?.insights).length === 0
  ) {
    console.log('No insights data to submit');
    return;
  }

  // Exit early if insights data has already been submitted
  const isSubmitted = isInsightsDataSubmitted();
  if (isSubmitted) {
    const submittedAt = get('insightsData.metadata.submitted') as number;
    console.log(
      'Insights data already submitted on:',
      new Date(submittedAt).toISOString()
    );
    return;
  }

  const metadata: Metadata = {
    studentName: SCORM.fullName,
    scormId: findScormId(),
    submittedAt: Date.now(),
    baseCourseId: getBaseCourseId()
  };

  if (isCourseScored()) {
    // we may get courses with insights data but no assessment attempts, so this is optional
    const assessmentAttempts =
      (get('assessment.attemptCount') as number) || null;

    // the submitted assessment attempts might be either the assessment attempt count or the average of all scored quiz attempts
    if (assessmentAttempts !== null) {
      metadata.assessmentAttempts = assessmentAttempts;
      console.log(
        `Assessment attempts (assessment.attemptCount): ${metadata.assessmentAttempts}`
      );
    } else {
      const averageAttempts = useQuizzes().averageQuizAttempts;
      if (averageAttempts.value > 0) {
        metadata.assessmentAttempts = averageAttempts.value;
        console.log(
          `Average attempts (averageQuizAttempts): ${metadata.assessmentAttempts}`
        );
      }
    }

    if (metadata.assessmentAttempts) {
      // store the assessment/quiz attempts
      set(
        `insightsData.metadata.assessmentAttempts`,
        metadata.assessmentAttempts
      );
    }

    if (insightsData.metadata.assessmentFirstScore) {
      metadata.assessmentFirstScore =
        insightsData.metadata.assessmentFirstScore;
    }

    metadata.assessmentScore = getScore();

    // also store the final score in suspend data
    set(`insightsData.metadata.assessmentScore`, metadata.assessmentScore);
  }

  const insightsArray: InsightsData[] = Object.values(insightsData.insights);
  const submissionData: SubmissionData = {
    metadata,
    insights: insightsArray
  };

  try {
    // send the insights data to mod-player via the SCORM API
    const success = sendInsightsData(submissionData) || false;

    if (success) {
      // store the submission timestamp in suspend data so we know the insights data was submitted
      set('insightsData.metadata.submitted', metadata.submittedAt);
      console.log(
        'Insights data submitted successfully on:',
        new Date(metadata.submittedAt).toISOString()
      );
    } else {
      console.warn('Insights data submission failed');
    }
  } catch (error) {
    console.error('Error submitting insights data:', error);
  }
}

export function isInsightsDataSubmitted(): boolean {
  const submittedAt = get(`insightsData.metadata.submitted`) as number;
  return submittedAt && submittedAt > 0 ? true : false;
}

function isCourseScored(): boolean {
  // check if the course has either an assessment of a scored quiz
  const content = useContent().compiled;
  const { statuses } = useQuizzes();
  const allScreens = content?.pages?.flatMap(page => page.screens);
  const hasAssessment =
    allScreens?.some(screen => screen?.template === 'assessment') || false;
  const scoredQuizzes =
    Object.values(statuses).filter(status => status.scored) || [];
  const hasScoredQuizzes = scoredQuizzes.length > 0;
  return hasAssessment || hasScoredQuizzes;
}
